export function rutify(rut: string) {
  const newRut = rut.replaceAll('.', '')
  if (newRut.includes('-')) {
    return newRut
  } else {
    return newRut.slice(0, -1) + '-' + newRut.slice(-1)
  }
}

/**
 * Formatea un RUT chileno al formato canónico `XX.XXX.XXX-X` (puntos de miles + guion).
 * Pensado para usarse on-blur en los formularios de ingreso: normaliza puntos/guion/espacios
 * y agrega los separadores de miles. Es idempotente (un RUT ya formateado se mantiene).
 *
 * Seguro para campos que también aceptan pasaporte (ej. AAA123456): si el valor contiene
 * cualquier letra distinta de K/k, se devuelve sin cambios para no corromper ese formato.
 * Valores vacíos o demasiado cortos (sin cuerpo + DV) también se devuelven tal cual.
 */
export function formatRut(rut: string): string {
  if (!rut) return rut
  // Pasaporte u otro identificador con letras (distintas de K) → no tocar
  if (/[a-jl-zA-JL-Z]/.test(rut)) return rut
  const clean = rut.replace(/[^0-9kK]/g, '')
  if (clean.length < 2) return rut
  const body = clean.slice(0, -1)
  const dv = clean.slice(-1).toUpperCase()
  if (!/^\d+$/.test(body)) return rut
  const bodyFmt = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  return `${bodyFmt}-${dv}`
}

/**
 * Normaliza un RUT para comparaciones de búsqueda: deja solo dígitos y el DV (0-9, k/K)
 * en minúscula, descartando puntos de miles, guion y espacios. Pensado para aplicarse a
 * AMBOS lados (valor almacenado y término tecleado) en los `checker` de las tablas, de modo
 * que la búsqueda funcione exista o no el autoformato (`formatRut`) sobre el valor guardado.
 */
export function normalizeRut(rut: unknown): string {
  return (rut ?? '')
    .toString()
    .replace(/[^0-9kK]/g, '')
    .toLowerCase()
}

/**
 * Comprueba si un valor de RUT coincide con un término de búsqueda de RUT.
 * Requiere que el término de búsqueda contenga al menos un dígito o 'k'/'K' normalizado.
 * Si rutSearch no contiene ningún carácter de RUT válido, retorna false (evita que includes("") coincida con todo).
 */
export function rutMatches(rutValue: unknown, rutSearch: unknown): boolean {
  const normSearch = normalizeRut(rutSearch)
  if (!normSearch) return false
  return normalizeRut(rutValue).includes(normSearch)
}

/**
 * Normaliza un texto para búsquedas: pasa a string, elimina tildes/diacríticos
 * (descomposición NFD + quita el rango de marcas combinantes U+0300–U+036F) y baja a minúsculas.
 * Es la misma transformación que los `checker` de las tablas aplicaban inline; se centraliza acá
 * para reutilizarla tanto al precomputar el campo `_search` de un ítem (una sola vez, al cargar)
 * como al normalizar el término tecleado, garantizando que ambos lados coincidan.
 */
export function normalizeSearch(value: unknown): string {
  return (value ?? '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

export function emailify(email: any): string {
  if (!email) return ''
  if (typeof email === 'object') {
    if ('@nil' in email) return ''
  }
  const sEmail = email.toString().trim()
  if (sEmail.includes('@nil') || sEmail === 'null') return ''
  return sEmail.toLowerCase()
}

export function phoneify(phone: any): string {
  if (!phone) return ''
  if (typeof phone === 'object') {
    if ('@nil' in phone) return ''
  }
  const sPhone = phone.toString().trim()
  if (sPhone.includes('@nil') || sPhone === 'null') return ''
  const noRegionCode = sPhone.replaceAll(' ', '').startsWith('+569')
    ? sPhone.slice(4)
    : sPhone.startsWith('569')
      ? sPhone.slice(3)
      : sPhone.startsWith('09') && sPhone.length === 10 // 09XXXXXXXX
        ? sPhone.slice(2)
        : sPhone.startsWith('9') && sPhone.length === 9 // 9XXXXXXXX
          ? sPhone.slice(1)
          : sPhone
  return noRegionCode.replace(/\D/g, '')
}

export function regionalPhoneify(phone: any): string {
  const cleanPhone = phoneify(phone)
  if (!cleanPhone) return ''
  return '+56 9 ' + cleanPhone
}

export function cleanNil<T>(val: any, fallback: T): T {
  if (val === null || val === undefined) return fallback
  if (typeof val === 'object' && ('@nil' in val || val['@nil'] === 'true')) return fallback
  if (typeof val === 'string' && (val.includes('@nil') || val === 'null')) return fallback
  return val as T
}

export function cleanNilNumber(val: any, fallback = 0): number {
  if (val === null || val === undefined) return fallback
  if (typeof val === 'object' && ('@nil' in val || val['@nil'] === 'true')) return fallback
  if (typeof val === 'string' && (val.includes('@nil') || val === 'null' || val.trim() === '')) return fallback
  const num = Number(val)
  return isNaN(num) ? fallback : num
}

export function parseSafeDate(dateVal: any): Date | null {
  if (!dateVal) return null
  if (dateVal instanceof Date) return isNaN(dateVal.getTime()) ? null : dateVal
  let s = String(dateVal).trim()
  if (s.includes('+') && !s.includes('T')) {
    s = s.split('+')[0]
  }
  const d = new Date(s)
  return isNaN(d.getTime()) ? null : d
}
